/** @jsx jsx */
import { type ComponentType, type CSSProperties, forwardRef, useCallback, Fragment } from 'react';
import { cssMap, jsx } from '@compiled/react';
import flow from 'lodash/flow';
import merge from 'lodash/merge';
import LeftArrow from '@atlaskit/icon/core/migration/arrow-left--arrow-left-circle';
import type { ItemState } from '@atlaskit/menu';
import {
	type CustomItemComponentProps,
	type CustomItemProps,
	NestingItem,
} from '@atlaskit/side-navigation';
import { SidebarErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { MenuItem } from '../../common/ui/menu-item/main.tsx';
import { cssFn as nestingItemArrow } from '../../common/ui/menu-item/utils/css-fn-utils/nesting-item-arrow-css-fn.tsx';
import { useSidebarTheme } from '../../controllers/sidebar-theme/index.tsx';
import { castRealCSSFnToWronglyTypedCSSFn, type LegacyEmotionCssObject } from '../../types.tsx';
import { isNormalClick } from '../../utils/is-normal-click.tsx';
import type { NestingMenuItemProps } from './types.tsx';

const iconSecondaryColorCssVar = '--nesting-menu-item-icon-secondary-color';

const styles = cssMap({
	backButtonThemeOverrides: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before], &:active span[data-item-elem-before]': {
			color: `var(${iconSecondaryColorCssVar})`,
		},
	},
});

const customCssFn: NonNullable<NestingMenuItemProps['cssFn']> =
	castRealCSSFnToWronglyTypedCSSFn<ItemState>((currentStateAndStyles): LegacyEmotionCssObject => {
		const activeColor =
			typeof currentStateAndStyles['&:active'] === 'object'
				? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					`${currentStateAndStyles['&:active'].color as any} !important`
				: 'inherit';

		const baseColor =
			typeof currentStateAndStyles['&:hover'] === 'object'
				? currentStateAndStyles['&:hover'].color
				: 'inherit';

		const newStyle = {
			...nestingItemArrow(currentStateAndStyles),

			/* fixes CSS leaking from the monolith */
			'&:visited, &:focus, &:active': {
				textDecoration: 'inherit',
				outline: 0,
			},
			/* fixes CSS leaking from the monolith */
			'&:active': {
				color: activeColor,
			},

			/* fixes CSS leaking from the monolith */
			'&:visited, &:focus': {
				color: baseColor,
			},

			'& [data-item-title]': {
				color: baseColor,
			},
		};
		return merge(currentStateAndStyles, newStyle);
	});

const WrappingLinkComponent: ComponentType<CustomItemComponentProps> = (
	props: CustomItemComponentProps,
) => <a {...props}>{props.children}</a>;

export const NestingMenuItem = forwardRef<HTMLElement, NestingMenuItemProps>(
	({ children, id, reloadOnNavigationToNesting, overrides = {}, title, ...rest }, ref) => {
		const theme = useSidebarTheme();
		const iconSecondaryColor = theme?.navigation?.icon?.secondary?.color?.default;
		const { BackMenuItem = {} } = overrides;
		const {
			title: backTitle = 'Back',
			wrapper: WrapperComponent = Fragment,
			...backItemProps
		} = BackMenuItem as NonNullable<NestingMenuItemProps['overrides']['BackMenuItem']>; // eslint-disable-line @typescript-eslint/consistent-type-assertions

		const renderBackItem = useCallback(
			({ onClick }: { onClick: () => void }) => (
				<WrapperComponent>
					<MenuItem
						{...backItemProps}
						analytics={{ itemId: 'back' }}
						iconBefore={<LeftArrow spacing="spacious" label="" />}
						onClick={(ev) => {
							if (isNormalClick(ev)) {
								onClick();
								backItemProps.onClick?.(ev);
							}
						}}
						css={iconSecondaryColor && styles.backButtonThemeOverrides}
						// eslint-disable-next-line jira/react/no-style-attribute, @typescript-eslint/consistent-type-assertions
						style={{ [iconSecondaryColorCssVar]: iconSecondaryColor ?? undefined } as CSSProperties}
					>
						{backTitle}
					</MenuItem>
				</WrapperComponent>
			),
			[WrapperComponent, backItemProps, backTitle, iconSecondaryColor],
		);

		const renderCustomItem = useCallback(
			({ children: itemTitle, onClick, cssFn: customItemCssFn, ...props }: CustomItemProps) => (
				<NestingItem
					{...props}
					id={id}
					component={reloadOnNavigationToNesting ? WrappingLinkComponent : props.component}
					onClick={(ev) => {
						// if nesting item already selected do not trigger route change
						if (props.isSelected === true) ev.preventDefault();
						onClick && onClick(ev);
					}}
					// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
					overrides={{ GoBackItem: { render: renderBackItem } }}
					title={itemTitle}
					// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
					cssFn={customItemCssFn ? flow([customItemCssFn, customCssFn]) : customCssFn}
				>
					<SidebarErrorBoundary id={`${id}Nesting`} packageName="jiraSpaAppsSidebarCommon">
						{children}
					</SidebarErrorBoundary>
				</NestingItem>
			),
			[children, id, reloadOnNavigationToNesting, renderBackItem],
		);

		return (
			<MenuItem {...rest} ref={ref} overrides={{ CustomItem: { render: renderCustomItem } }}>
				{title}
			</MenuItem>
		);
	},
);
